<script setup>
import { onMounted, ref, watch } from 'vue'
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'
import mapboxgl from 'mapbox-gl'

const props = defineProps({
  placeholder: { type: String, default: 'Search Location' },
  modelValue: { type: String, default: '' }
})

const emit = defineEmits(['update:modelValue', 'select'])

const container = ref(null)


  let geocoder;

  onMounted(() => {
    const token = import.meta.env.VITE_MAPBOX_TOKEN
    if (!token) return
  
    geocoder = new MapboxGeocoder({
      accessToken: token,
      mapboxgl: mapboxgl,
      placeholder: props.placeholder,
      types: 'country,region,place,locality,neighborhood', // Limit to places initially
      marker: false
    })
  
    geocoder.addTo(container.value)
  
    // Pre-fill if value exists
    if (props.modelValue) {
      geocoder.setInput(props.modelValue)
    }
  
    geocoder.on('result', (e) => {
      const { result } = e
      emit('update:modelValue', result.place_name)
      emit('select', {
        center: result.center, // [lng, lat]
        place_name: result.place_name,
        bbox: result.bbox
      })
    })
  })

  // Watch for external updates
  watch(() => props.modelValue, (newVal) => {
      if (geocoder && newVal !== undefined && newVal !== null) {
          geocoder.setInput(newVal)
      }
  })
</script>

<template>
  <div ref="container" class="mapbox-geocoder-wrapper w-full"></div>
</template>

<style>
/* Custom styling to match our app */
/* Custom styling to match our app */
.mapbox-geocoder-wrapper .mapboxgl-ctrl-geocoder {
  width: 100%;
  max-width: none;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05); /* shadow-sm */
  border: 1px solid #e2e8f0; /* slate-200 */
  border-radius: 0.75rem; /* rounded-xl (matching TextInput) */
  font-family: inherit;
  background-color: white;
  color: #0f172a; /* slate-900 */
  transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

/* Dark Mode Overrides */
:where(.dark) .mapbox-geocoder-wrapper .mapboxgl-ctrl-geocoder {
  background-color: #0f172a; /* slate-900 */
  border-color: #334155; /* slate-700 */
  color: white;
}
:where(.dark) .mapbox-geocoder-wrapper .mapboxgl-ctrl-geocoder input {
  color: white !important; /* Force input text color */
}
:where(.dark) .mapbox-geocoder-wrapper .mapboxgl-ctrl-geocoder ::placeholder {
  color: #64748b; /* slate-500 */
}

.mapbox-geocoder-wrapper .mapboxgl-ctrl-geocoder:focus-within {
  outline: 2px solid transparent;
  outline-offset: 2px;
  border-color: #3b82f6; /* blue-500 */
  --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
  --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(3px + var(--tw-ring-offset-width)) var(--tw-ring-color);
  box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000);
  --tw-ring-opacity: 1;
  --tw-ring-color: rgb(59 130 246 / var(--tw-ring-opacity)); /* blue-500 */
}

/* Specific inner spacing */
.mapbox-geocoder-wrapper .mapboxgl-ctrl-geocoder--input {
  padding: 0.625rem 1rem 0.625rem 2.5rem; /* Match TextInput padding */
  height: auto; /* Allow auto height */
}

.mapbox-geocoder-wrapper .mapboxgl-ctrl-geocoder--icon {
  top: 12px; /* Adjust for taller input */
  fill: #94a3b8; /* slate-400 */
}
</style>
